import { useRef, useState } from 'react';
import { Alert, Button, Card, Collapse, ConfigProvider, Space, Table, Tag } from 'antd';
import { DownloadOutlined, FileSearchOutlined } from '@ant-design/icons';
import { Page } from 'components/page/page';
import { generateHeader, postViaAuth, SERVER } from 'apis/api';
import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import gregorian from 'react-date-object/calendars/gregorian';
import persian_fa from 'react-date-object/locales/persian_fa';
import gregorian_en from 'react-date-object/locales/gregorian_en';
import './financial-reports.scss';

import type { IncomeReport as Report, IncomeReportAsset, IncomeReportDay, IncomeReportRate, IncomeReportWithdrawal } from './income-types';

const amount = (value: number | null | undefined, decimals = 2) => value == null ? 'نامشخص'
    : value.toLocaleString('fa-IR', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
const date = (value: string) => new Intl.DateTimeFormat('fa-IR', { timeZone: 'Asia/Tehran' })
    .format(new Date(`${value}T12:00:00Z`));

export default function IncomeReport() {
    const [range, setRange] = useState<string[]>([]);
    const [report, setReport] = useState<Report>();
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');
    const operation = useRef(false);
    const valid = range.length === 2 && range[0] <= range[1];

    const generate = async () => {
        if (!valid || !range || operation.current) return;
        operation.current = true;
        setLoading(true);
        setError('');
        setReport(undefined);
        try {
            // Send calendar labels; the server applies Tehran midnight and historical DST.
            const response = await postViaAuth<Report>('Admin/Dashboard/IncomeReport', {
                startDate: range[0],
                endDate: range[1],
            });
            if (response.status < 200 || response.status >= 300 || !response.value) {
                setError(response.message || 'ساخت گزارش ناموفق بود؛ بازه تاریخ و دسترسی خود را بررسی و دوباره تلاش کنید.');
                return;
            }
            setReport(response.value);
        } catch {
            setError('ساخت گزارش ناموفق بود؛ بازه تاریخ و دسترسی خود را بررسی و دوباره تلاش کنید.');
        } finally {
            operation.current = false;
            setLoading(false);
        }
    };

    const download = async () => {
        if (!report || operation.current) return;
        operation.current = true;
        setDownloading(true);
        setError('');
        const failure = 'دانلود PDF ناموفق بود. دوباره تلاش کنید.';
        try {
            const response = await fetch(`${SERVER}Admin/Dashboard/IncomeReport`, {
                method: 'POST',
                headers: await generateHeader({ 'Content-Type': 'application/json', Accept: 'application/pdf' }),
                body: JSON.stringify({ startDate: report.startDate, endDate: report.endDate }),
            });
            if (!response.ok) {
                const error = await response.json().catch(() => null);
                setError(error?.message || failure);
                return;
            }
            if (!response.headers.get('Content-Type')?.includes('application/pdf')) {
                setError(failure);
                return;
            }
            const url = URL.createObjectURL(await response.blob());
            const link = document.createElement('a');
            link.href = url;
            link.download = `PMXChange-Income-${report.startDate}-${report.endDate}.pdf`;
            link.click();
            window.setTimeout(URL.revokeObjectURL.bind(URL, url), 1000);
        } catch {
            setError(failure);
        } finally {
            setDownloading(false);
            operation.current = false;
        }
    };

    const withdrawalIssues = report?.withdrawals.filter(w => w.missingFeeCount > 0 || w.missingPriceCount > 0).map(w => {
        const reasons = [];
        if (w.missingFeeCount > 0) reasons.push(`${amount(w.missingFeeCount, 0)} برداشت بدون کارمزد ثبت‌شده`);
        const estimatedCount = w.fallbackPriceCount ?? 0;
        const unresolvedCount = w.missingPriceCount - estimatedCount;
        if (unresolvedCount > 0) reasons.push(`${amount(unresolvedCount, 0)} برداشت بدون نرخ تبدیل تاریخی`);
        if (estimatedCount > 0) {
            const observedAt = w.fallbackObservedAt ? new Intl.DateTimeFormat('fa-IR', {
                dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Tehran',
            }).format(new Date(w.fallbackObservedAt)) : '';
            reasons.push(`کارمزد ${amount(estimatedCount, 0)} برداشت (${amount(w.fallbackFeesNative, 6)} TRX) با نرخ جایگزین ${amount(w.fallbackRate, 8)} تتر از ${w.fallbackSource}${observedAt ? `، دریافت‌شده در ${observedAt} به وقت تهران` : ''} برآورد شد.`);
        }
        return `${w.symbol}: ${reasons.join('، ')}`;
    }).join('؛ ') ?? '';
    const fallbackRates = report?.rates.filter(r => r.isFallback || r.rate == null).length ?? 0;
    const summary: { key: string; title: string; irt: number | null; usdt: number | null; note?: string }[] = report ? [
        ...report.groups.map(g => ({ key: g.key, title: `اسپرد ${g.title}`, irt: g.spreadIrt, usdt: g.spreadUsdt })),
        { key: 'spread', title: 'جمع اسپردها', irt: report.spreadIrt, usdt: report.spreadUsdt },
        { key: 'commission', title: 'کارمزد معاملات', irt: report.commissionIrt, usdt: report.commissionUsdt },
        { key: 'withdrawals', title: 'کارمزد برداشت تتر و ترون', irt: report.withdrawalFeesIrt, usdt: report.withdrawalFeesUsdt, note: withdrawalIssues },
        { key: 'total', title: 'جمع درآمد ناخالص', irt: report.totalIrt, usdt: report.totalUsdt,
            note: report.totalUsdt == null ? `جمع بخش‌های قابل محاسبه: ${amount(report.knownTotalUsdt)} تتر؛ جمع کامل به تکمیل اطلاعات ناقص وابسته است.` : undefined },
    ] : [];

    return <ConfigProvider direction="rtl"><Page title="گزارش درآمد" className="financial-reports income-report"><div id="pmx-income-report" className="financial-report-content">
        <Card title="بازه گزارش">
            <div className="report-intro">
                <p className="text-base leading-8 text-slate-800">درآمد صرافی را در بازه دلخواه بررسی کنید.</p>
                <p className="text-sm leading-7 text-slate-600">اسپرد UUSD، تتر و سایر رمزارزها، کارمزد معاملات و برداشت‌ها به تفکیک محاسبه می‌شوند.</p>
            </div>
            <div className="report-filters">
                <div className="min-w-0 space-y-3">
                    <label htmlFor="income-report-range" className="text-sm font-semibold text-slate-800">بازه تاریخ شمسی</label>
                    <DatePicker range calendar={persian} locale={persian_fa} disabled={loading || downloading}
                        id="income-report-range" placeholder="انتخاب تاریخ شروع و پایان"
                        inputClass="rmdp-input income-report-date-input"
                        containerStyle={{ display: 'block', width: '100%' }}
                        calendarPosition="bottom-end" portal zIndex={1100}
                        onChange={value => {
                            setRange(Array.isArray(value) ? value.map(v => new DateObject(v).convert(gregorian).setLocale(gregorian_en).format('YYYY-MM-DD')) : []);
                            setReport(undefined); setError('');
                        }} />
                    <p className="text-xs leading-6 text-slate-600">روز شروع و پایان به‌طور کامل و به وقت تهران محاسبه می‌شوند.</p>
                </div>
                <div className="flex min-w-0 flex-col justify-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                    <span className="text-sm font-semibold text-emerald-800">تبدیل با نرخ تتر همان روز</span>
                    <p className="text-xs leading-6 text-slate-600">نرخ هر روز، میانگین موزون خرید تتر صرافی از کاربران در همان روز است. روز بدون خرید تتر، نرخ نزدیک‌ترین روز قبل را می‌گیرد.</p>
                </div>
            </div>
            <div className="report-controls">
                <Button size="large" className="!h-12 !px-7" type="primary" icon={<FileSearchOutlined aria-hidden="true" />} loading={loading} disabled={!valid || downloading} onClick={() => void generate()}>محاسبه گزارش</Button>
                <Button size="large" className="!h-12 !px-7" icon={<DownloadOutlined aria-hidden="true" />} loading={downloading} disabled={!report || loading} onClick={() => void download()}>دانلود PDF</Button>
            </div>
            {error && <Alert type="error" showIcon message={error} className="mb-4" />}
            {report && <>
                <Space wrap className="mb-4"><Tag color="green">{date(report.startDate)} تا {date(report.endDate)}</Tag>
                    <span>{amount(report.orderCount, 0)} معامله بسته‌شده</span>
                    {fallbackRates > 0 && <span className="income-report-warning">{amount(fallbackRates, 0)} روز با نرخ جایگزین یا نامشخص</span>}</Space>
                {report.totalUsdt == null && <Alert className="mb-4" type="warning" showIcon message="جمع کامل درآمد به علت اطلاعات ناقص نامشخص است."
                    description={`جمع بخش‌های قابل محاسبه: ${amount(report.knownTotalUsdt)} تتر. روزهای بدون نرخ خرید و برداشت‌های ناقص در جزئیات مشخص شده‌اند.`} />}
                {report.orderCount === 0 && <Alert className="mb-4" type="info" showIcon message="در این بازه معامله تومانی بسته‌شده‌ای وجود ندارد." />}
                <Table rowKey="key" pagination={false} dataSource={summary} scroll={{ x: 600 }} columns={[
                    { title: 'منبع درآمد', dataIndex: 'title', render: (_, row) => <div className="space-y-2 py-1"><span>{row.title}</span>
                        {row.note && <p className="max-w-xl text-xs leading-6 income-report-warning">{row.note}</p>}</div> },
                    { title: 'تومان', dataIndex: 'irt', render: value => amount(value, 0) },
                    { title: 'معادل تتر / دلار', dataIndex: 'usdt', render: value => amount(value) },
                ]} />
                <Alert className="mt-4" type="info" showIcon message="این گزارش درآمد ناخالص است."
                    description="هزینه واقعی شبکه، هزینه تأمین موجودی، کارمزد بازار خارجی و هزینه‌های عملیاتی کسر نشده‌اند. کارمزد معاملات، جمع کارمزد ثبت‌شده هر سفارش است و معادل دلاری با قرارداد یک تتر برابر یک دلار نمایش داده می‌شود." />
            </>}
        </Card>
        {report && <>
            <Card title="نرخ‌ها و اسپرد روزانه">
                <Table<IncomeReportDay> rowKey={r => `${r.date}:${r.key}`} dataSource={report.days} scroll={{ x: 1150 }} pagination={false} columns={[
                    { title: 'روز', dataIndex: 'date', render: date },
                    { title: 'گروه', dataIndex: 'title' },
                    { title: 'خرید کاربر', dataIndex: 'customerBuyCount', render: v => amount(v, 0) },
                    { title: 'فروش کاربر', dataIndex: 'customerSellCount', render: v => amount(v, 0) },
                    { title: 'خرید موزون صرافی (تومان)', dataIndex: 'exchangeAverageBuyRate', render: v => amount(v) },
                    { title: 'فروش موزون صرافی (تومان)', dataIndex: 'exchangeAverageSellRate', render: v => amount(v) },
                    { title: 'اسپرد (تومان)', dataIndex: 'spreadIrt', render: v => amount(v, 0) },
                    { title: 'کارمزد (تومان)', dataIndex: 'commissionIrt', render: v => amount(v, 0) },
                    { title: 'نرخ تتر روز', dataIndex: 'usdtRate', render: v => amount(v, 0) },
                    { title: 'توضیح', dataIndex: 'issue', render: v => v || '—' },
                ]} />
            </Card>
            <Card title="کارمزد برداشت‌های موفق">
                <Table<IncomeReportWithdrawal> rowKey="symbol" dataSource={report.withdrawals} pagination={false} scroll={{ x: 800 }} columns={[
                    { title: 'ارز', dataIndex: 'symbol' },
                    { title: 'تعداد', dataIndex: 'count', render: v => amount(v, 0) },
                    { title: 'کارمزد به ارز اصلی', dataIndex: 'feesNative', render: v => amount(v, 6) },
                    { title: 'معادل تتر', dataIndex: 'feesUsdt', render: v => amount(v) },
                    { title: 'معادل تومان', dataIndex: 'feesIrt', render: v => amount(v, 0) },
                    { title: 'بدون کارمزد ثبت‌شده', dataIndex: 'missingFeeCount', render: v => amount(v, 0) },
                    { title: 'بدون نرخ تاریخی', dataIndex: 'missingPriceCount', render: v => amount(v, 0) },
                    { title: 'با نرخ جایگزین', dataIndex: 'fallbackPriceCount', render: v => amount(v ?? 0, 0) },
                ]} />
                <p className="mt-3 text-sm">مبنای بازه برداشت، زمان تسویه است. UUSD کنار گذاشته می‌شود. تبدیل کارمزد TRX با نرخ ثبت‌شده روی تراکنش انجام می‌شود. برای برداشت‌های بدون نرخ تاریخی، نرخ جایگزین KuCoin به کار می‌رود. معادل تومان با نرخ تتر روز تسویه محاسبه می‌شود.</p>
            </Card>
            <Collapse defaultActiveKey={['method']} items={[{ key: 'method', label: 'روش محاسبه و جزئیات بیشتر', children: <div className="space-y-4">
                <p>نرخ تبدیل هر روز، میانگین موزون نرخ خرید تتر صرافی از کاربران در همان روز است؛ روز بدون خرید تتر، نرخ نزدیک‌ترین روز قبل را می‌گیرد.</p>
                <p>اسپرد برابر حجم فروش صرافی ضرب‌در اختلاف نرخ فروش با میانگین موزون خرید همان روز و گروه است. این میانگین برای کل فروش، حتی در صورت عدم توازن، اعمال می‌شود. اگر روزی خرید از کاربر نداشته باشد، اسپرد فروش آن روز نامشخص است.</p>
                <p>برای UUSD و تتر، مقدار ارز و نرخ ثبت‌شده مبناست. برای سایر رمزارزها، حجم دلاری از مبلغ تومان تقسیم بر نرخ ثبت‌شده بازسازی می‌شود. اختلاف تسویه با مقدار ضرب‌در نرخ، به اسپرد اضافه نمی‌شود.</p>
                <Table rowKey="key" pagination={false} dataSource={report.groups} scroll={{ x: 550 }} columns={[
                    { title: 'گروه', dataIndex: 'title' },
                    { title: 'کارمزد (تومان)', dataIndex: 'commissionIrt', render: v => amount(v, 0) },
                    { title: 'کارمزد (تتر)', dataIndex: 'commissionUsdt', render: v => amount(v) },
                ]} />
                <Table<IncomeReportAsset> rowKey="symbol" pagination={false} dataSource={report.assets} scroll={{ x: 650 }} columns={[
                    { title: 'رمزارز', dataIndex: 'symbol' },
                    { title: 'خرید کاربر', dataIndex: 'customerBuyCount', render: v => amount(v, 0) },
                    { title: 'فروش کاربر', dataIndex: 'customerSellCount', render: v => amount(v, 0) },
                    { title: 'اسپرد (تومان)', dataIndex: 'spreadIrt', render: v => amount(v, 0) },
                    { title: 'کارمزد (تومان)', dataIndex: 'commissionIrt', render: v => amount(v, 0) },
                ]} />
                <Table<IncomeReportRate> rowKey="date" pagination={false} dataSource={report.rates} scroll={{ x: 550 }} columns={[
                    { title: 'روز', dataIndex: 'date', render: date },
                    { title: 'نرخ تتر (تومان)', dataIndex: 'rate', render: v => amount(v, 0) },
                    { title: 'منبع نرخ', dataIndex: 'sourceDate', render: (v, row) => row.rate == null ? 'بدون نرخ ثبت‌شده' : row.isFallback ? `جایگزین از ${date(v)}` : 'همان روز' },
                ]} />
            </div> }]} />
        </>}
    </div></Page></ConfigProvider>;
}
